import { useState } from 'react'
import StatusBadge from './StatusBadge'
import StatusDropdown from './StatusDropdown'
import { updateRequestStatus } from '../../lib/adminQueries'
import { supabase } from '../../lib/supabase'
import { parseTimeToDisplay } from '../../utils/timeSlots'
import './RequestsTable.css'

function RequestsTable({ data, type, onStatusUpdate }) {
  const [expandedRows, setExpandedRows] = useState(new Set())

  const toggleRow = (id) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedRows(newExpanded)
  }

  const handleStatusChange = async (id, newStatus) => {
    try {
      const tableName = type === 'nomination' ? 'nomination_requests' : 'service_requests'
      const { error } = await updateRequestStatus(tableName, id, newStatus)

      if (error) throw error

      // Send status update email to customer
      const request = data.find((r) => r.id === id)
      if (request) {
        let emailType = null

        if (type === 'service' && (newStatus === 'confirmed' || newStatus === 'rejected' || newStatus === 'cancelled')) {
          emailType = 'service_status_update'
        } else if (type === 'nomination' && (newStatus === 'completed' || newStatus === 'cancelled')) {
          emailType = 'nomination_status_update'
        }

        if (emailType) {
          try {
            supabase.functions.invoke('send-notification-email', {
              body: {
                type: emailType,
                data: { ...request, new_status: newStatus },
              },
            })
          } catch (emailError) {
            console.error('Email notification failed:', emailError)
          }
        }
      }

      if (onStatusUpdate) {
        onStatusUpdate()
      }
    } catch (error) {
      console.error('Failed to update status:', error)
      alert('Failed to update status. Please try again.')
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }


  if (!data || data.length === 0) {
    return (
      <div className="empty-state">
        <p>No requests found</p>
      </div>
    )
  }

  return (
    <div className="requests-table-container">
      {/* Desktop Table View */}
      <table className="requests-table desktop-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Contact</th>
            {type === 'nomination' ? (
              <>
                <th>Surgery</th>
                <th>Postcode</th>
              </>
            ) : (
              <>
                <th>Service</th>
                <th>Preferred Date and Time</th>
              </>
            )}
            <th>Status</th>
            <th>Submitted</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.map((request) => (
            <>
              <tr key={request.id} className={expandedRows.has(request.id) ? 'expanded' : ''}>
                <td>
                  <div className="name-cell">
                    {request.first_name} {request.last_name}
                  </div>
                </td>
                <td>
                  <div className="contact-cell">
                    <div>{request.email}</div>
                    <div className="phone">{request.phone}</div>
                  </div>
                </td>
                {type === 'nomination' ? (
                  <>
                    <td>{request.surgery_name}</td>
                    <td>{request.postcode}</td>
                  </>
                ) : (
                  <>
                    <td>{request.service?.replace('-', ' ')}</td>
                    <td>{formatDate(request.preferred_date)} {parseTimeToDisplay(request.preferred_time)}</td>
                  </>
                )}
                <td>
                  <StatusDropdown
                    currentStatus={request.status || 'pending'}
                    onStatusChange={(newStatus) => handleStatusChange(request.id, newStatus)}
                    type={type}
                  />
                </td>
                <td>{formatDate(request.created_at)}</td>
                <td>
                  <button
                    onClick={() => toggleRow(request.id)}
                    className="details-button"
                  >
                    {expandedRows.has(request.id) ? 'Hide' : 'Details'}
                  </button>
                </td>
              </tr>
              {expandedRows.has(request.id) && (
                <tr className="details-row">
                  <td colSpan="8">
                    <div className="details-content">
                      <div className="details-grid">
                        <div className="detail-item">
                          <strong>Full Name:</strong>
                          <span>{request.first_name} {request.last_name}</span>
                        </div>
                        <div className="detail-item">
                          <strong>Date of Birth:</strong>
                          <span>{formatDate(request.date_of_birth)}</span>
                        </div>
                        <div className="detail-item">
                          <strong>Email:</strong>
                          <span>{request.email}</span>
                        </div>
                        <div className="detail-item">
                          <strong>Phone:</strong>
                          <span>{request.phone}</span>
                        </div>
                        {type === 'nomination' && (
                          <>
                            <div className="detail-item">
                              <strong>Address:</strong>
                              <span>
                                {request.address_line_1}
                                {request.address_line_2 && `, ${request.address_line_2}`}
                                , {request.city}, {request.postcode}
                              </span>
                            </div>
                            <div className="detail-item">
                              <strong>Surgery:</strong>
                              <span>{request.surgery_name}</span>
                            </div>
                          </>
                        )}
                        {type === 'service' && (
                          <>
                            <div className="detail-item">
                              <strong>Service:</strong>
                              <span>{request.service?.replace('-', ' ')}</span>
                            </div>
                            <div className="detail-item">
                              <strong>Preferred Date and Time:</strong>
                              <span>{formatDate(request.preferred_date)} {parseTimeToDisplay(request.preferred_time)}</span>
                            </div>
                          </>
                        )}
                        {request.additional_notes && (
                          <div className="detail-item full-width">
                            <strong>Additional Notes:</strong>
                            <span>{request.additional_notes}</span>
                          </div>
                        )}
                        <div className="detail-item">
                          <strong>Consent:</strong>
                          <span>{request.consent ? 'Yes' : 'No'}</span>
                        </div>
                        <div className="detail-item">
                          <strong>Submitted:</strong>
                          <span>{new Date(request.created_at).toLocaleString('en-GB')}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>

      {/* Mobile Card View */}
      <div className="mobile-cards">
        {data.map((request) => (
          <div key={request.id} className="request-card">
            <div className="card-header">
              <h3>{request.first_name} {request.last_name}</h3>
              <StatusBadge status={request.status || 'pending'} />
            </div>
            <div className="card-body">
              <div className="card-row">
                <strong>Email:</strong>
                <span>{request.email}</span>
              </div>
              <div className="card-row">
                <strong>Phone:</strong>
                <span>{request.phone}</span>
              </div>
              {type === 'nomination' ? (
                <>
                  <div className="card-row">
                    <strong>Surgery:</strong>
                    <span>{request.surgery_name}</span>
                  </div>
                  <div className="card-row">
                    <strong>Postcode:</strong>
                    <span>{request.postcode}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="card-row">
                    <strong>Service:</strong>
                    <span>{request.service?.replace('-', ' ')}</span>
                  </div>
                  <div className="card-row">
                    <strong>Date & Time:</strong>
                    <span>{formatDate(request.preferred_date)} {parseTimeToDisplay(request.preferred_time)}</span>
                  </div>
                </>
              )}
              <div className="card-row">
                <strong>Status:</strong>
                <StatusDropdown
                  currentStatus={request.status || 'pending'}
                  onStatusChange={(newStatus) => handleStatusChange(request.id, newStatus)}
                  type={type}
                />
              </div>
              <div className="card-row">
                <strong>Submitted:</strong>
                <span>{formatDate(request.created_at)}</span>
              </div>
            </div>
            <div className="card-footer">
              <button
                onClick={() => toggleRow(request.id)}
                className="details-button"
              >
                {expandedRows.has(request.id) ? 'Hide Details' : 'View Details'}
              </button>
            </div>
            {expandedRows.has(request.id) && (
              <div className="card-details">
                <div className="detail-item">
                  <strong>DOB:</strong>
                  <span>{formatDate(request.date_of_birth)}</span>
                </div>
                {type === 'nomination' && (
                  <div className="detail-item">
                    <strong>Address:</strong>
                    <span>
                      {request.address_line_1}
                      {request.address_line_2 && `, ${request.address_line_2}`}
                      , {request.city}, {request.postcode}
                    </span>
                  </div>
                )}
                {request.additional_notes && (
                  <div className="detail-item">
                    <strong>Notes:</strong>
                    <span>{request.additional_notes}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default RequestsTable
